/**
 * Parse Honcho representation markdown into structured format.
 *
 * Honcho returns different formats depending on scope:
 * - peer.representation(): Deductive + Inductive sections (no UUIDs)
 * - session.representation(): Explicit section with UUIDs
 *
 * Deductive format:
 *   [2026-01-27 17:54:12] Content here
 *      Premises:
 *      - premise 1
 *
 * Inductive format:
 *   **Pattern** [high]: Description here
 *      **Type**: behavior
 *      **Sources**:
 *      - source 1
 *
 * Explicit format (session-scoped only):
 *   [2026-01-27 16:54:54] 548e4ce1-3429-4006-99dd-445aa3f97c45's name is Benjamin
 */

export interface ParsedRepresentation {
  representation: {
    explicit: Array<{ content: string }>
    deductive: Array<{ conclusion: string; premises: string[] }>
  }
}

export function parseRepresentationMarkdown(markdown: string): ParsedRepresentation {
  const explicit: Array<{ content: string }> = []
  const deductive: Array<{ conclusion: string; premises: string[] }> = []

  if (!markdown || typeof markdown !== 'string') {
    return { representation: { explicit, deductive } }
  }

  const lines = markdown.split('\n')
  let currentSection: 'explicit' | 'deductive' | 'inductive' | null = null

  for (const line of lines) {
    const trimmed = line.trim()

    // Detect section headers (## Deductive Observations, etc.)
    if (trimmed.startsWith('#')) {
      const lower = trimmed.toLowerCase()
      if (lower.includes('explicit')) {
        currentSection = 'explicit'
      } else if (lower.includes('deductive')) {
        currentSection = 'deductive'
      } else if (lower.includes('inductive')) {
        currentSection = 'inductive'
      }
      continue
    }

    // Skip empty lines and metadata
    if (
      !trimmed ||
      trimmed.startsWith('Premises:') ||
      trimmed.startsWith('**Type**') ||
      trimmed.startsWith('**Sources**') ||
      trimmed.startsWith('-')
    ) {
      continue
    }

    // Deductive: [timestamp] content
    if (currentSection === 'deductive' && trimmed.startsWith('[')) {
      const match = trimmed.match(/^\[[^\]]+\]\s*(.+)$/)
      if (match) {
        deductive.push({ conclusion: match[1], premises: [] })
      }
    }
    // Inductive: **Pattern** [confidence]: content (treated as deductive for viz)
    else if (currentSection === 'inductive' && trimmed.startsWith('**Pattern**')) {
      const match = trimmed.match(/^\*\*Pattern\*\*\s*\[[^\]]+\]:\s*(.+)$/)
      if (match) {
        deductive.push({ conclusion: match[1], premises: [] })
      }
    }
    // Explicit: [timestamp] uuid content (session-scoped representation only)
    else if (currentSection === 'explicit' && trimmed.startsWith('[')) {
      const match = trimmed.match(/^\[[^\]]+\]\s+([a-f0-9-]{36})\s*(.*)$/i)
      if (match) {
        const content = match[2] || ''
        explicit.push({ content: content.startsWith("'s") ? `user${content}` : content })
      }
    }
  }

  return { representation: { explicit, deductive } }
}
